import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { toast } from "@/shared/hooks/useToast";
import { useState } from "react";
import { CollectionsSizeSearchCard } from "../components/CollectionsSizeSearchCard";
import { CollectionsSizeSummary as CollectionsSizeSummaryView } from "../components/CollectionsSizeSummary";
import { useCollectionsSizeColumns } from "../hooks/useCollectionsSizeColumns";
import { useCollectionSizesQuery } from "../hooks/useCollectionSizes";
import type { CollectionsSizeSearchParams } from "../types/collection-types";
import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { Button } from "@/shared/components/ui/button";

export function CollectionsSizePage() {
  const columns = useCollectionsSizeColumns();

  // draft form
  const [draft, setDraft] = useState<CollectionsSizeSearchParams>({
    start_date: "",
    end_date: "",
    dw_account_id: "",
    channel: "",
    report_type: "",
  });

  // submitted params (검색 실행한 값)
  const [params, setParams] = useState<CollectionsSizeSearchParams>({
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 50,
    order_by: "row_count",
    is_desc: true,
  });
  const [searchPerformed, setSearchPerformed] = useState(false);

  // API 데이터 조회
  const { data, isLoading, isError, refetch } = useCollectionSizesQuery(params, searchPerformed);

  const handleSearch = () => {
    if (!draft.start_date || !draft.end_date) {
      toast({ title: "조회 기간을 선택해주세요.", variant: "destructive" });
      return;
    }
    if (draft.start_date > draft.end_date) {
      toast({ title: "시작일이 종료일보다 클 수 없습니다.", variant: "destructive" });
      return;
    }

    setParams({
      start_date: draft.start_date,
      end_date: draft.end_date,
      dw_account_id: draft.dw_account_id?.trim() || undefined,
      channel: draft.channel?.trim() || undefined,
      report_type: draft.report_type?.trim() || undefined,
      page: 1,
      page_size: 50,
      order_by: "row_count",
      is_desc: true,
    });
    setSearchPerformed(true);
  };

  const handleReset = () => {
    setDraft({
      start_date: "",
      end_date: "",
      dw_account_id: "",
      channel: "",
      report_type: "",
    });
    setParams({
      start_date: "",
      end_date: "",
      page: 1,
      page_size: 50,
      order_by: "row_count",
      is_desc: true,
    });
    setSearchPerformed(false);
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }));
  };

  return (
    <div className="space-y-4">
      <Breadcrumbs />
      <PageHeader
        title="수집 데이터 통계"
        description="프리즘에서 수집한 파일들의 행 수와 파일 크기를 조회합니다."
      />

      <CollectionsSizeSearchCard
        value={draft}
        onChange={setDraft}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {searchPerformed && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : isError || !data ? (
            <EmptyState
              title="데이터를 불러올 수 없습니다"
              description="오류가 발생했습니다. 다시 시도해주세요."
              action={
                <Button variant="outline" onClick={() => refetch()}>
                  다시 시도
                </Button>
              }
            />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <CollectionsSizeSummaryView summary={data.summary} />
              </div>

              <DataTable
                columns={columns}
                data={data.rows}
                totalCount={data.total_count}
                page={data.page}
                pageSize={data.page_size}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={false}
                isError={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}